import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import GameIcon from "components/admin/GameIcon";

/**
 * Ô số nguyên. Giữ chuỗi rỗng khi người dùng xoá hết để con trỏ không bị nhảy,
 * nhưng trả ra ngoài luôn là số.
 */
export function NumField({ label, value, onChange, min, max, step, helper, disabled }) {
  return (
    <MDInput
      type="number"
      size="small"
      fullWidth
      disabled={disabled}
      label={label}
      helperText={helper}
      value={value === null || value === undefined ? "" : value}
      inputProps={{ min, max, step }}
      onChange={(event) => {
        const text = event.target.value;
        if (text === "") {
          onChange(0);
          return;
        }
        let next = Number(text);
        if (Number.isNaN(next)) return;
        if (min !== undefined) next = Math.max(min, next);
        if (max !== undefined) next = Math.min(max, next);
        onChange(next);
      }}
    />
  );
}

NumField.defaultProps = {
  min: 0,
  max: undefined,
  step: 1,
  helper: null,
  disabled: false,
};

NumField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  helper: PropTypes.node,
  disabled: PropTypes.bool,
};

/** Ô chọn trong một danh sách đã tải sẵn (hình xăm, danh hiệu, bị động). value là id hoặc null. */
export function ListPicker({ label, value, options, onChange, idKey, iconKey, fallback, note }) {
  const selected = options.find((o) => o[idKey] === value) || null;
  return (
    <Autocomplete
      fullWidth
      size="small"
      value={selected}
      options={options}
      getOptionLabel={(o) => `${o.name} (#${o[idKey]})`}
      isOptionEqualToValue={(a, b) => a[idKey] === b[idKey]}
      noOptionsText="Không tìm thấy"
      onChange={(event, option) => onChange(option ? option[idKey] : null)}
      renderOption={(props, option) => (
        <MDBox component="li" {...props} key={option[idKey]} display="flex" gap={1}>
          {iconKey && <GameIcon path={option[iconKey]} size={26} fallback={fallback} />}
          <MDBox lineHeight={1.1} minWidth={0}>
            <MDTypography variant="button" display="block">
              {`${option.name} (#${option[idKey]})`}
            </MDTypography>
            {note && note(option) && (
              <MDTypography variant="caption" color="text" display="block">
                {note(option)}
              </MDTypography>
            )}
          </MDBox>
        </MDBox>
      )}
      renderInput={(params) => <MDInput {...params} label={label} />}
    />
  );
}

ListPicker.defaultProps = {
  value: null,
  idKey: "id",
  iconKey: null,
  fallback: "category",
  note: null,
};

ListPicker.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  idKey: PropTypes.string,
  iconKey: PropTypes.string,
  fallback: PropTypes.string,
  note: PropTypes.func,
};

/** Ô chọn đơn giản từ mảng [{ value, label }]. */
export function Choice({ label, value, options, onChange }) {
  return (
    <MDInput
      select
      size="small"
      fullWidth
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </MDInput>
  );
}

Choice.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};
